import {
    Inviter,
    Invitation,
    InviterInviteOptions,
    Registerer,
    RegistererState,
    Session,
    SessionState,
    URI,
    UserAgent,
    UserAgentOptions
} from "sip.js";

export type LineRegistrationStatus =
    | "idle"
    | "starting"
    | "started"
    | "registering"
    | "registered"
    | "unregistered"
    | "stopped"
    | "error";

export type CallDirection = "incoming" | "outgoing";

export type CallStatus =
    | "new"
    | "ringing"
    | "establishing"
    | "established"
    | "held"
    | "ended"
    | "failed";

export interface SipLineConfig {
    id: string;
    sipUri: string;
    wsServer: string;
    host: string;
    authorizationUsername: string;
    authorizationPassword: string;
    displayName?: string;
    autoRegister?: boolean;
    extraUserAgentOptions?: Partial<UserAgentOptions>;
}

export interface LineStateSnapshot {
    id: string;
    sipUri: string;
    host: string;
    displayName?: string;
    registrationStatus: LineRegistrationStatus;
    transportConnected: boolean;
    lastError?: string | null;
    activeCallId?: string | null;
    hasIncomingCall: boolean;
    hasEstablishedCall: boolean;
}

export interface CallStateSnapshot {
    id: string;
    lineId: string;
    direction: CallDirection;
    remoteTarget: string;
    callerId: string;
    dialedNumber?: string | null;
    status: CallStatus;
    isHeld: boolean;
    isMuted: boolean;
    startedAt?: Date;
    endedAt?: Date;
}

export interface SipPhoneStateSnapshot {
    lines: LineStateSnapshot[];
    calls: CallStateSnapshot[];
    activeCall?: CallStateSnapshot | null;
    hasAnyCall: boolean;
    hasAnyRingingCall: boolean;
    hasAnyEstablishedCall: boolean;
    hasAnyHeldCall: boolean;
}

type StateListener = (state: SipPhoneStateSnapshot) => void;

interface ManagedLine {
    config: SipLineConfig;
    uri: URI;
    userAgent: UserAgent;
    registerer: Registerer;
    registrationStatus: LineRegistrationStatus;
    transportConnected: boolean;
    lastError?: string | null;
    activeCallId?: string | null;
}

interface ManagedCall {
    id: string;
    lineId: string;
    session: Session;
    direction: CallDirection;
    remoteTarget: string;
    callerId: string;
    dialedNumber?: string | null;
    status: CallStatus;
    isHeld: boolean;
    isMuted: boolean;
    startedAt?: Date;
    endedAt?: Date;
}

export class SipPhoneManager {

    public static instance = new SipPhoneManager();


    private readonly lines = new Map<string, ManagedLine>();
    private readonly calls = new Map<string, ManagedCall>();
    private readonly listeners = new Set<StateListener>();


    private isAudioUnlocked = false;
    private isUnlockingAudio = false;
    private globalUnlockInstalled = false;

    constructor() {
        // Register global one-shot user-gesture listeners as soon as the manager is created,
        // so audio is unlocked on the very first interaction anywhere on the page
        // (browser autoplay policy requires a user gesture before <audio> can play).
        if (typeof window !== "undefined") {
            this.installGlobalUnlockListeners();
        }
    }

    private installGlobalUnlockListeners(): void {
        if (this.globalUnlockInstalled) return;
        this.globalUnlockInstalled = true;

        const events: Array<keyof WindowEventMap> = [
            "pointerdown",
            "mousedown",
            "touchstart",
            "keydown"
        ];

        const handler = () => {
            void this.unlockAudio().finally(() => {
                if (this.isAudioUnlocked) {
                    for (const ev of events) {
                        window.removeEventListener(ev, handler, true);
                    }
                }
            });
        };

        for (const ev of events) {
            window.addEventListener(ev, handler, true);
        }
    }

    private ringtoneElementId = "ringtone";
    private ringtoneInterval: number | null = null;
    private ringtoneAudioUrl: string | null = null;

    public setRingtoneElementId(elementId: string): void {
        this.ringtoneElementId = elementId;
    }

    public setRingtoneSource(src: string): void {
        this.ringtoneAudioUrl = src;

        const audio = document.getElementById(this.ringtoneElementId) as HTMLAudioElement | null;
        if (audio) {
            audio.src = src;
            audio.load();
        }
    }

    private remoteAudioElementId = "maincall";
    private remoteMediaStream: MediaStream | null = null;
    private localMediaStream: MediaStream | null = null;

    public setRemoteAudioElementId(elementId: string): void {
        this.remoteAudioElementId = elementId;
    }

    public subscribe(listener: StateListener): () => void {
        this.listeners.add(listener);
        listener(this.getState());

        return () => {
            this.listeners.delete(listener);
        };
    }

    public getState(): SipPhoneStateSnapshot {
        const lineStates: LineStateSnapshot[] = Array.from(this.lines.values()).map((line) => {
            const call = line.activeCallId ? this.calls.get(line.activeCallId) : undefined;

            return {
                id: line.config.id,
                sipUri: line.config.sipUri,
                host: line.config.host,
                displayName: line.config.displayName,
                registrationStatus: line.registrationStatus,
                transportConnected: line.transportConnected,
                lastError: line.lastError ?? null,
                activeCallId: line.activeCallId ?? null,
                hasIncomingCall: !!call && call.direction === "incoming" && ["new", "ringing"].includes(call.status),
                hasEstablishedCall: !!call && ["established", "held"].includes(call.status)
            };
        });

        const callStates: CallStateSnapshot[] = Array.from(this.calls.values()).map((call) => ({
            id: call.id,
            lineId: call.lineId,
            direction: call.direction,
            remoteTarget: call.remoteTarget,
            callerId: call.callerId,
            dialedNumber: call.dialedNumber,
            status: call.status,
            isHeld: call.isHeld,
            isMuted: call.isMuted,
            startedAt: call.startedAt,
            endedAt: call.endedAt
        }));

        const activeCall =
            callStates.find((x) => x.status === "established") ||
            callStates.find((x) => x.status === "held") ||
            callStates.find((x) => x.status === "establishing") ||
            callStates.find((x) => x.status === "ringing") ||
            callStates.find((x) => x.status === "new") ||
            null;

        return {
            lines: lineStates,
            calls: callStates,
            activeCall,
            hasAnyCall: callStates.some((x) => !["ended", "failed"].includes(x.status)),
            hasAnyRingingCall: callStates.some((x) => x.status === "ringing" || x.status === "new"),
            hasAnyEstablishedCall: callStates.some((x) => x.status === "established"),
            hasAnyHeldCall: callStates.some((x) => x.status === "held")
        };
    }

    public getActiveCall(): CallStateSnapshot | null {
        return this.getState().activeCall ?? null;
    }

    public async unlockAudio(): Promise<void> {
        if (this.isAudioUnlocked || this.isUnlockingAudio) {
            return;
        }

        this.isUnlockingAudio = true;

        try {
            // IMPORTANT: only unlock via the ringtone element. We must NOT touch the
            // remote call element (maincall) here. That element is the live call
            // audio sink; the unlock fires on the very first user gesture, which can
            // be the "Answer" click, and pausing/muting/resetting maincall would race
            // with bindStreamToAudioElement and kill the caller's audio. Playing real
            // media on the ringtone element is enough to activate document-level audio
            // for the whole page, which covers the WebRTC call audio too.
            const audio = document.getElementById(this.ringtoneElementId) as HTMLAudioElement | null;
            if (!audio) {
                // Element not mounted yet — leave unlocked=false so the next gesture retries.
                return;
            }

            // If the ringtone is already playing, audio is clearly unlocked already.
            // Don't disturb an in-progress ring.
            if (!audio.paused) {
                this.isAudioUnlocked = true;
                return;
            }

            const originalVolume = audio.volume;
            const originalMuted = audio.muted;

            // Play REAL media during the gesture so the browser activates its audio
            // output pipeline (a zero-length/silent source does NOT count as genuine
            // playback in any browser). Keep it silent with volume=0 — every browser
            // honors volume=0 reliably, whereas Safari ignores `muted` set right before
            // play() (which used to leak an audible ringtone blip on tab refocus).
            audio.muted = true;
            audio.volume = 0;

            try {
                await audio.play();
                this.isAudioUnlocked = true;
            } catch {
                // Ignore autoplay unlock errors here; next gesture will retry.
            }

            audio.pause();
            audio.currentTime = 0;
            audio.muted = originalMuted;
            audio.volume = originalVolume;
        } finally {
            this.isUnlockingAudio = false;
        }
    }

    public async addLine(config: SipLineConfig): Promise<void> {
        if (this.lines.has(config.id)) {
            throw new Error(`Line with id '${config.id}' already exists.`);
        }

        const uri = UserAgent.makeURI(config.sipUri);
        if (!uri) {
            throw new Error(`Invalid SIP URI for line '${config.id}'.`);
        }

        const options: UserAgentOptions = {
            uri,
            displayName: config.displayName,
            authorizationUsername: config.authorizationUsername,
            authorizationPassword: config.authorizationPassword,
            transportOptions: {
                server: config.wsServer
            },
            ...config.extraUserAgentOptions
        };

        const userAgent = new UserAgent(options);
        const registerer = new Registerer(userAgent);

        const line: ManagedLine = {
            config,
            uri,
            userAgent,
            registerer,
            registrationStatus: "idle",
            transportConnected: false,
            lastError: null,
            activeCallId: null
        };

        userAgent.delegate = {
            onInvite: (invitation: Invitation) => {
                this.handleIncomingInvitation(config.id, invitation);
            }
        };

        userAgent.transport.onConnect = () => {
            line.transportConnected = true;
            if (line.registrationStatus === "starting") {
                line.registrationStatus = "started";
            }
            this.emit();
        };

        userAgent.transport.onDisconnect = (error?: Error) => {
            line.transportConnected = false;
            if (error) {
                line.lastError = error.message;
            }
            this.emit();
        };

        registerer.stateChange.addListener((state: RegistererState) => {
            switch (state) {
                case RegistererState.Initial:
                    line.registrationStatus = "idle";
                    break;
                case RegistererState.Registered:
                    line.registrationStatus = "registered";
                    break;
                case RegistererState.Unregistered:
                    line.registrationStatus = "unregistered";
                    break;
                case RegistererState.Terminated:
                    line.registrationStatus = "stopped";
                    break;
            }
            this.emit();
        });

        this.lines.set(config.id, line);
        this.emit();

        await this.startLine(config.id);

        if (config.autoRegister !== false) {
            await this.registerLine(config.id);
        }
    }

    public async startLine(lineId: string): Promise<void> {
        const line = this.getLineOrThrow(lineId);

        if (line.registrationStatus === "starting" || line.transportConnected) {
            return;
        }

        line.registrationStatus = "starting";
        line.lastError = null;
        this.emit();

        try {
            await line.userAgent.start();
            if (!line.transportConnected) {
                line.registrationStatus = "started";
            }
            this.emit();
        } catch (error) {
            line.registrationStatus = "error";
            line.lastError = this.errorMessage(error);
            this.emit();
            throw error;
        }
    }

    public async registerLine(lineId: string): Promise<void> {
        const line = this.getLineOrThrow(lineId);

        line.registrationStatus = "registering";
        line.lastError = null;
        this.emit();

        try {
            await line.registerer.register();
        } catch (error) {
            line.registrationStatus = "error";
            line.lastError = this.errorMessage(error);
            this.emit();
            throw error;
        }
    }

    public async unregisterLine(lineId: string): Promise<void> {
        const line = this.getLineOrThrow(lineId);

        try {
            await line.registerer.unregister();
        } catch (error) {
            line.lastError = this.errorMessage(error);
            this.emit();
            throw error;
        }
    }

    public async removeLine(lineId: string): Promise<void> {
        const line = this.getLineOrThrow(lineId);

        const lineCalls = this.getCallsByLine(lineId);
        for (const call of lineCalls) {
            await this.hangup(call.id).catch(() => undefined);
        }

        await this.unregisterLine(lineId).catch(() => undefined);
        await line.userAgent.stop().catch(() => undefined);

        this.lines.delete(lineId);
        this.emit();
    }


    // Asked before every outgoing call. Resolves to a message when the call must not be made
    // (the account has no minutes left and no wallet to pay with), or to nothing when it may.
    // Set by Global.ts, which owns the HUB client; the check never runs while the call rings.
    public beforeDial?: () => Promise<string | undefined>

    public async dial(number: string, lineId?: string): Promise<string> {

        const selectedLineId = lineId ?? this.getDefaultLineId();
        const line = this.getLineOrThrow(selectedLineId);

        // No minutes and no money means no call at all -- not one that shows "Connecting" and
        // drops. The message is shown by the hook; the error stops the caller's flow.
        if (this.beforeDial) {
            const refused = await this.beforeDial();
            if (refused) throw Object.assign(new Error(refused), { name: "CallRefused" });
        }

        const normalizedNumber = this.normalizeDialNumber(number);
        const target = `sip:${normalizedNumber}@${line.uri.host}`;

        return await this.makeCall(selectedLineId, target);
    }

    private getDefaultLineId(): string {
        const firstLine = this.lines.values().next().value as ManagedLine | undefined;

        if (!firstLine) {
            throw new Error("No SIP lines are available.");
        }

        return firstLine.config.id;
    }

    private normalizeDialNumber(number: string): string {
        if (!number) return "";


        let raw = number.replace(/[^\d+]/g, "");

        if (raw.startsWith("+1")) {
            return "9" + raw.substring(2);
        }

        if (raw.startsWith("+90")) {
            return "9" + raw.substring(1);
        }

        if (raw.startsWith("+972")) {
            return "9" + raw.substring(1);
        }

        const digits = raw.replace(/\D/g, "");

        if (digits.length === 10) {
            return "9" + digits;
        }

        return digits;
    }

    public async makeCall(lineId: string, target: string): Promise<string> {
        const line = this.getLineOrThrow(lineId);

        const targetUri = UserAgent.makeURI(target);
        if (!targetUri) {
            throw new Error(`Invalid target URI: ${target}`);
        }

        await this.ensureLocalMedia();

        const inviter = new Inviter(line.userAgent, targetUri);

        const callId = this.createCallId();
        const call: ManagedCall = {
            id: callId,
            lineId,
            session: inviter,
            direction: "outgoing",
            remoteTarget: target,
            callerId: this.normalizeCallerId(target),
            status: "new",
            isHeld: false,
            isMuted: false
        };

        this.attachSessionEvents(call);
        this.calls.set(callId, call);
        line.activeCallId = callId;
        this.emit();

        try {
            call.status = "ringing";
            this.emit();

            const inviteOptions: InviterInviteOptions = {
                sessionDescriptionHandlerOptions: {
                    constraints: {
                        audio: true,
                        video: false
                    }
                }
            };

            await inviter.invite(inviteOptions);
            return callId;
        } catch (error) {
            call.status = "failed";
            call.endedAt = new Date();
            line.lastError = this.errorMessage(error);
            this.emit();
            throw error;
        }
    }

    public async answerCall(callId: string): Promise<void> {
        const call = this.getCallOrThrow(callId);

        if (!(call.session instanceof Invitation)) {
            throw new Error("Only incoming calls can be answered.");
        }

        await this.ensureLocalMedia();

        call.status = "establishing";
        this.emit();

        this.stopRingtone();

        try {
            await call.session.accept({
                sessionDescriptionHandlerOptions: {
                    constraints: {
                        audio: true,
                        video: false
                    }
                }
            });
        } catch (error) {
            call.status = "failed";
            call.endedAt = new Date();
            this.emit();
            throw error;
        }
    }

    public async rejectCall(callId: string): Promise<void> {
        const call = this.getCallOrThrow(callId);

        if (!(call.session instanceof Invitation)) {
            throw new Error("Only incoming calls can be rejected.");
        }

        await call.session.reject();
        call.status = "ended";
        call.endedAt = new Date();
        this.clearActiveLineCall(call);
        this.cleanupMediaIfNoActiveCalls();
        this.emit();
        this.stopRingtone();
    }

    public async sendDtmf(callId: string, tone: string, duration = 160): Promise<void> {
        const call = this.getCallOrThrow(callId);

        if (!tone || tone.length !== 1) {
            throw new Error("DTMF tone must be a single character.");
        }

        const normalizedTone = tone.toUpperCase();

        if (!/^[0-9A-D#*]$/.test(normalizedTone)) {
            throw new Error(`Invalid DTMF tone: ${tone}`);
        }

        if (
            call.session.state !== SessionState.Established &&
            !call.isHeld
        ) {
            throw new Error("DTMF can only be sent on an established or held call.");
        }

        const sessionAny = call.session as any;

        if (typeof sessionAny.info !== "function") {
            throw new Error("This SIP.js session does not support INFO for DTMF.");
        }

        await sessionAny.info({
            requestOptions: {
                body: {
                    contentDisposition: "render",
                    contentType: "application/dtmf-relay",
                    content: `Signal=${normalizedTone}\r\nDuration=${duration}`
                }
            }
        });
    }

    public async hangup(callId: string): Promise<void> {
        const call = this.getCallOrThrow(callId);
        const session = call.session as any;

        try {
            if (call.session.state === SessionState.Initial && typeof session.cancel === "function") {
                await session.cancel();
            } else if (
                call.session.state === SessionState.Establishing &&
                typeof session.cancel === "function"
            ) {
                await session.cancel();
            } else if (
                call.session.state === SessionState.Established &&
                typeof session.bye === "function"
            ) {
                await session.bye();
            } else if (typeof session.dispose === "function") {
                await session.dispose();
            } else if (typeof session.terminate === "function") {
                await session.terminate();
            }
        } finally {
            call.status = "ended";
            call.endedAt = new Date();
            this.clearActiveLineCall(call);
            this.cleanupMediaIfNoActiveCalls();
            this.emit();
            this.stopRingtone();
        }
    }

    public async holdCall(callId: string): Promise<void> {
        const call = this.getCallOrThrow(callId);

        await this.applyHold(call.session, true);
        call.status = "held";
        call.isHeld = true;
        this.emit();
    }

    public async unholdCall(callId: string): Promise<void> {
        const call = this.getCallOrThrow(callId);

        await this.applyHold(call.session, false);
        call.status = "established";
        call.isHeld = false;
        this.emit();
    }

    public muteCall(callId: string): void {
        const call = this.getCallOrThrow(callId);

        const pc: RTCPeerConnection | undefined =
            (call.session as any).sessionDescriptionHandler?.peerConnection;

        if (!pc) return;

        pc.getSenders().forEach((sender: RTCRtpSender) => {
            if (sender.track && sender.track.kind === "audio") {
                sender.track.enabled = false;
            }
        });

        call.isMuted = true;
        this.emit();
    }

    public unmuteCall(callId: string): void {
        const call = this.getCallOrThrow(callId);

        const pc: RTCPeerConnection | undefined =
            (call.session as any).sessionDescriptionHandler?.peerConnection;

        if (!pc) return;

        pc.getSenders().forEach((sender: RTCRtpSender) => {
            if (sender.track && sender.track.kind === "audio") {
                sender.track.enabled = true;
            }
        });

        call.isMuted = false;
        this.emit();
    }

    public async holdCurrentAndAnswer(lineIdToAnswer: string, incomingCallId: string): Promise<void> {
        const activeEstablishedCalls = Array.from(this.calls.values()).filter(
            (c) => c.status === "established" && c.id !== incomingCallId
        );

        for (const call of activeEstablishedCalls) {
            await this.holdCall(call.id);
        }

        const incomingCall = this.getCallOrThrow(incomingCallId);
        if (incomingCall.lineId !== lineIdToAnswer) {
            throw new Error("The incoming call does not belong to the requested line.");
        }

        await this.answerCall(incomingCallId);
    }

    public async blindTransfer(callId: string, target: string): Promise<void> {
        const call = this.getCallOrThrow(callId);
        const targetUri = UserAgent.makeURI(target);

        if (!targetUri) {
            throw new Error(`Invalid transfer target URI: ${target}`);
        }

        const session = call.session as any;
        if (typeof session.refer !== "function") {
            throw new Error("This SIP.js session does not support refer().");
        }

        await session.refer(targetUri);
    }

    public async blindTransferToExtension(callId: string, extension: string): Promise<void> {
        const call = this.getCallOrThrow(callId);

        if (!extension?.trim()) {
            throw new Error("Extension is required.");
        }

        const normalizedExtension = extension.trim().replace(/\D/g, "");
        if (!normalizedExtension) {
            throw new Error(`Invalid extension: ${extension}`);
        }

        const line = this.getLineOrThrow(call.lineId);
        const target = `sip:${normalizedExtension}@${line.uri.host}`;

        await this.blindTransfer(callId, target);
        await this.hangup(callId);
    }

    public async attendedTransfer(sourceCallId: string, targetCallId: string): Promise<void> {
        const sourceCall = this.getCallOrThrow(sourceCallId);
        const targetCall = this.getCallOrThrow(targetCallId);

        const session = sourceCall.session as any;
        if (typeof session.refer !== "function") {
            throw new Error("This SIP.js session does not support refer().");
        }

        await session.refer(targetCall.session as any);
    }

    public hasLine(lineId: string): boolean {
        return this.lines.has(lineId);
    }

    public async clearAllLines(): Promise<void> {
        const lineIds = Array.from(this.lines.keys());

        for (const lineId of lineIds) {
            await this.removeLine(lineId).catch(() => undefined);
        }

        this.emit();
    }

    public hasLines(): boolean {
        return (this.getState().lines?.length ?? 0) > 0;
    }
    public getLines(): LineStateSnapshot[] | undefined {
        return this.getState().lines;
    }

    public getLine(lineId: string): LineStateSnapshot | undefined {
        return this.getState().lines.find((x) => x.id === lineId);
    }

    public getCall(callId: string): CallStateSnapshot | undefined {
        return this.getState().calls.find((x) => x.id === callId);
    }

    public getCallsForLine(lineId: string): CallStateSnapshot[] {
        return this.getState().calls.filter((x) => x.lineId === lineId);
    }

    private handleIncomingInvitation(lineId: string, invitation: Invitation): void {
        const line = this.getLineOrThrow(lineId);

        const remoteTarget =
            invitation.remoteIdentity.uri?.toString?.() ||
            invitation.remoteIdentity.displayName ||
            "Unknown";

        const incomingNumber =
            invitation.remoteIdentity.uri?.user?.toString?.() ||
            invitation.remoteIdentity.displayName ||
            remoteTarget;

        const dialedNumber = this.extractDialedNumber(invitation);

        const call: ManagedCall = {
            id: this.createCallId(),
            lineId,
            session: invitation,
            direction: "incoming",
            remoteTarget,
            callerId: this.normalizeCallerId(incomingNumber),
            dialedNumber,
            status: "ringing",
            isHeld: false,
            isMuted: false
        };

        this.attachSessionEvents(call);

        this.calls.set(call.id, call);
        line.activeCallId = call.id;
        this.emit();
        this.startRingtone();
    }

    private attachSessionEvents(call: ManagedCall): void {
        this.attachRemoteMedia(call.session);

        call.session.stateChange.addListener((state: SessionState) => {
            switch (state) {
                case SessionState.Initial:
                    call.status = "new";
                    break;
                case SessionState.Establishing:
                    call.status = "establishing";
                    this.attachRemoteMedia(call.session);
                    break;
                case SessionState.Established:
                    call.status = call.isHeld ? "held" : "established";
                    if (!call.startedAt) {
                        call.startedAt = new Date();
                    }
                    this.attachRemoteMedia(call.session);
                    this.stopRingtone();
                    break;
                case SessionState.Terminated:
                    call.status = "ended";
                    call.endedAt = new Date();
                    this.clearActiveLineCall(call);
                    this.cleanupMediaIfNoActiveCalls();
                    this.stopRingtone();
                    break;
            }

            this.emit();
        });
    }

    private attachRemoteMedia(session: Session): void {
        const sessionAny = session as any;
        const peerConnection: RTCPeerConnection | undefined =
            sessionAny.sessionDescriptionHandler?.peerConnection;

        if (!peerConnection) {
            return;
        }

        const bindTracks = () => {
            const remoteStream = new MediaStream();

            peerConnection.getReceivers().forEach((receiver: RTCRtpReceiver) => {
                if (receiver.track) {
                    remoteStream.addTrack(receiver.track);
                }
            });

            this.remoteMediaStream = remoteStream;
            this.bindStreamToAudioElement(remoteStream);
        };

        peerConnection.ontrack = () => {
            bindTracks();
        };

        bindTracks();
    }

    private bindStreamToAudioElement(stream: MediaStream): void {
        const audio = document.getElementById(this.remoteAudioElementId) as HTMLAudioElement | null;
        if (!audio) {
            console.warn(`Audio element with id '${this.remoteAudioElementId}' was not found.`);
            return;
        }

        audio.autoplay = true;
        //audio.inline = true;

        if (audio.srcObject !== stream) {
            audio.srcObject = stream;
        }

        void audio.play().catch((error) => {
            console.error("Failed to play remote audio:", error);
        });
    }

    private async ensureLocalMedia(): Promise<void> {
        if (this.localMediaStream) {
            return;
        }

        this.localMediaStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false
        });
    }

    private cleanupMediaIfNoActiveCalls(): void {
        const hasActiveCalls = Array.from(this.calls.values()).some(
            (x) => !["ended", "failed"].includes(x.status)
        );

        if (hasActiveCalls) {
            return;
        }

        const audio = document.getElementById(this.remoteAudioElementId) as HTMLAudioElement | null;
        if (audio) {
            audio.pause();
            audio.srcObject = null;
        }

        this.remoteMediaStream = null;

        if (this.localMediaStream) {
            this.localMediaStream.getTracks().forEach((track) => track.stop());
            this.localMediaStream = null;
        }
    }

    private async applyHold(session: Session, hold: boolean): Promise<void> {
        const s = session as any;

        if (session.state !== SessionState.Established) {
            throw new Error("Call must be established before hold/unhold.");
        }

        s.sessionDescriptionHandlerOptionsReInvite = {
            hold
        };

        if (typeof s.invite === "function") {
            await s.invite({
                requestDelegate: {
                    onAccept: () => {
                        // Hold/unhold accepted
                    },
                    onReject: () => {
                        throw new Error("Hold/unhold re-INVITE was rejected.");
                    }
                }
            });
            return;
        }

        if (typeof s.reinvite === "function") {
            await s.reinvite({
                requestDelegate: {
                    onAccept: () => {
                        // Hold/unhold accepted
                    },
                    onReject: () => {
                        throw new Error("Hold/unhold re-INVITE was rejected.");
                    }
                }
            });
            return;
        }

        throw new Error("No supported hold/unhold strategy found for this SIP.js version.");
    }

    private getLineOrThrow(lineId: string): ManagedLine {
        const line = this.lines.get(lineId);
        if (!line) {
            throw new Error(`Line '${lineId}' not found.`);
        }
        return line;
    }

    private getCallOrThrow(callId: string): ManagedCall {
        const call = this.calls.get(callId);
        if (!call) {
            throw new Error(`Call '${callId}' not found.`);
        }
        return call;
    }

    private getCallsByLine(lineId: string): ManagedCall[] {
        return Array.from(this.calls.values()).filter((x) => x.lineId === lineId);
    }

    private clearActiveLineCall(call: ManagedCall): void {
        const line = this.lines.get(call.lineId);
        if (!line) return;

        if (line.activeCallId === call.id) {
            line.activeCallId = null;
        }
    }

    private createCallId(): string {
        return `call_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }

    private errorMessage(error: unknown): string {
        if (error instanceof Error) {
            return error.message;
        }
        return String(error);
    }


    private startRingtone(): void {
        const audio = document.getElementById(this.ringtoneElementId) as HTMLAudioElement | null;
        if (!audio) {
            console.warn(`Audio element with id '${this.ringtoneElementId}' was not found.`);
            return;
        }

        if (this.ringtoneAudioUrl && audio.src !== this.ringtoneAudioUrl) {
            audio.src = this.ringtoneAudioUrl;
            audio.load();
        }

        this.stopRingtone();

        const playOnce = () => {
            audio.pause();
            audio.currentTime = 0;
            void audio.play().catch((error) => {
                console.error("Failed to play ringtone:", error);
            });
        };

        playOnce();

        this.ringtoneInterval = window.setInterval(() => {
            playOnce();
        }, 4000);
    }

    private stopRingtone(): void {
        if (this.ringtoneInterval !== null) {
            window.clearInterval(this.ringtoneInterval);
            this.ringtoneInterval = null;
        }

        const audio = document.getElementById(this.ringtoneElementId) as HTMLAudioElement | null;
        if (audio) {
            audio.pause();
            audio.currentTime = 0;
        }
    }



    private extractDialedNumber(invitation: Invitation): string | null {
        const anyInv = invitation as any;
        const message = anyInv.request?.message ?? anyInv.request;
        if (!message) return null;

        const getHeader = (name: string): string | null => {
            try {
                if (typeof message.getHeader === "function") {
                    const v = message.getHeader(name);
                    if (v) return v;
                }
                if (typeof message.parseHeader === "function") {
                    const v = message.parseHeader(name);
                    if (v?.uri?.user) return `sip:${v.uri.user}@x`;
                }
            } catch { }
            return null;
        };

        const extractUserFromSipHeader = (raw: string): string | null => {
            const m = raw.match(/sip:([^@>;,\s]+)@/i);
            return m?.[1] ?? null;
        };

        // X-DID first: it is the one our own inbound routes set, so it is authoritative.
        // The rest are best-effort fallbacks — note Diversion carries the forwarding party
        // rather than the dialed number, so it must never win over X-DID.
        const candidates: Array<{ name: string; isSip: boolean }> = [
            { name: "X-DID", isSip: false },
            { name: "X-Called-Number", isSip: false },
            { name: "Diversion", isSip: true },
            { name: "P-Called-Party-ID", isSip: true },
            { name: "History-Info", isSip: true },
        ];

        const toE164Us = (input: string): string | null => {
            const digits = input.replace(/\D/g, "");
            if (digits.length === 10) return `+1${digits}`;
            if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
            if (digits.length > 11) {
                // already has country code; preserve with +
                return `+${digits}`;
            }
            return null;
        };

        for (const c of candidates) {
            const raw = getHeader(c.name);
            if (!raw) continue;
            const user = c.isSip ? extractUserFromSipHeader(raw) : raw.trim();
            if (!user) continue;
            const e164 = toE164Us(user);
            if (e164) return e164;
        }

        return null;
    }

    private normalizeCallerId(value: string): string {
        const raw = this.extractUserPart(value);
        const cleaned = this.cleanDialedNumber(raw);

        if (!cleaned) {
            return value;
        }

        if (this.isExtension(cleaned)) {
            return cleaned;
        }

        const usFormatted = this.formatUsPhoneNumber(cleaned);
        return usFormatted || cleaned;
    }

    private extractUserPart(value: string): string {
        if (!value) return "";

        const trimmed = value.trim();

        if (trimmed.startsWith("sip:")) {
            const withoutSip = trimmed.slice(4);
            const atIndex = withoutSip.indexOf("@");
            return atIndex >= 0 ? withoutSip.slice(0, atIndex) : withoutSip;
        }

        const atIndex = trimmed.indexOf("@");
        if (atIndex >= 0) {
            return trimmed.slice(0, atIndex);
        }

        return trimmed;
    }

    private cleanDialedNumber(value: string): string {
        if (!value) return "";

        let digits = value.replace(/\D/g, "");

        // Remove common outbound prefixes from the beginning
        // Adjust this list any time based on your PBX routes
        const outboundPrefixes = ["9"];

        // Only remove prefix for long/public numbers, not short extensions
        if (digits.length > 7) {
            for (const prefix of outboundPrefixes) {
                if (digits.startsWith(prefix) && digits.length > prefix.length + 7) {
                    digits = digits.slice(prefix.length);
                    break;
                }
            }
        }

        // Remove leading US country code if present
        if (digits.length === 11 && digits.startsWith("1")) {
            digits = digits.slice(1);
        }

        return digits;
    }

    private isExtension(value: string): boolean {
        return /^\d{2,6}$/.test(value);
    }

    private formatUsPhoneNumber(value: string): string | null {
        if (!/^\d{10}$/.test(value)) {
            return null;
        }

        const area = value.slice(0, 3);
        const prefix = value.slice(3, 6);
        const line = value.slice(6);

        return `(${area}) ${prefix}-${line}`;
    }



    private emit(): void {
        const snapshot = this.getState();

        this.listeners.forEach((listener) => {
            listener(snapshot);
        });
    }
}